import React, { useEffect, useRef, useState } from "react";

export interface MenuItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  divider?: boolean;
}

interface ButtonWithPopoverProps {
  button: React.ReactNode | ((isOpen: boolean) => React.ReactNode);
  menuItems: MenuItem[];
  placement?: "left" | "right" | "center";
  align?: "top" | "bottom";
  className?: string;
  popoverClassName?: string;
  closeOnItemClick?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const ButtonWithPopover: React.FC<ButtonWithPopoverProps> = ({
  button,
  menuItems,
  placement = "right",
  align = "bottom",
  className = "",
  popoverClassName = "",
  closeOnItemClick = true,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    onOpenChange?.(newIsOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
    onOpenChange?.(false);
  };

  const handleMenuItemClick = (item: MenuItem) => {
    if (item.disabled) return;
    item.onClick();
    if (closeOnItemClick) {
      handleClose();
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popoverRef.current &&
        buttonRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen]);

  // Get placement classes
  const getPlacementClasses = () => {
    const placementClasses = {
      left: "right-0",
      right: "left-0",
      center: "left-1/2 -translate-x-1/2",
    };

    const alignClasses = {
      top: "bottom-full mb-2",
      bottom: "top-full mt-2",
    };

    return `${placementClasses[placement]} ${alignClasses[align]}`;
  };

  const renderButton = () => {
    if (typeof button === "function") {
      return button(isOpen);
    }
    return button;
  };

  return (
    <div className={`relative ${className}`}>
      {/* Button */}
      <div ref={buttonRef} onClick={handleToggle}>
        {renderButton()}
      </div>

      {/* Popover */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Popover Content */}
          <div
            ref={popoverRef}
            className={`absolute ${getPlacementClasses()} z-50 min-w-[200px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden ${popoverClassName}`}
            role="menu"
            aria-orientation="vertical"
          >
            <div className="py-1">
              {menuItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  {item.divider && index > 0 && (
                    <div className="border-t border-gray-200 my-1" />
                  )}
                  <button
                    onClick={() => handleMenuItemClick(item)}
                    disabled={item.disabled}
                    className={`
                      w-full text-left px-4 py-2 text-sm text-gray-700 
                      hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                      transition-colors duration-150
                      flex items-center gap-2
                      ${item.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    `}
                    role="menuitem"
                  >
                    {item.icon && (
                      <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                        {item.icon}
                      </span>
                    )}
                    <span className="flex-1">{item.label}</span>
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ButtonWithPopover;

